import React, { useEffect, useRef, useState } from "react";

import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { init as initAnalytics, trackPageView } from "./lib/analytics";
import { COOKIE_CONSENT_EVENT } from "./lib/cookieConsent";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ParentAuthProvider, useParentAuth } from "./context/ParentAuthContext";
import { StudentAuthProvider, useStudentAuth } from "./context/StudentAuthContext";
import { Card, EmptyState, Spinner } from "./components/ui/primitives";
import { ShieldAlert } from "lucide-react";
import BillingGate from "./components/BillingGate";
import ErrorBoundary from "./components/ErrorBoundary";
import NotFound from "./components/NotFound";
import InstallPWA from "./components/InstallPWA";
import { I18nProvider } from "./lib/i18n";
import { PlatformAccessProvider, usePlatformAccess } from "./context/PlatformAccessContext";
import PlatformAccessState from "./components/platform/PlatformAccessState";
import { isTenantlessPlatformUser } from "./lib/platformAccess";
import { InternalNoIndex } from "./components/marketing/MarketingSeo";
import { SOLUTION_KEYS } from "./pages/solutions/solutionRegistry";

// ONLY the marketing landing stays eager (fast first paint on the "/" scan path); everything
// else — including the post-auth app chrome (AppLayout → GlobalSearch/AiraDrawer/recordRegistry/
// moduleArt) and the auth screens — is route code-split (React.lazy) so the marketing entry
// bundle no longer ships the whole app (MARKETING_10X_PLAN Phase-2 §1).
import Landing from "./pages/Landing";
import { getTenantSlug } from "./lib/tenant";
import { resolveHomeRoute } from "./lib/homeRoute";
import { hasEmployeeServiceWorkspaceAccess } from "./lib/employeeService";
import { hasLearningDevelopmentWorkspaceAccess, hasPerformanceWorkspaceAccess } from "./lib/workspaceAccess";
import { isHrPayrollExperiencePath } from "./lib/hrPayrollExperience";
import {
  hasRequiredSessionFeatures,
  requiredHrPayrollEntitlements,
} from "./lib/hrPayrollEntitlements";
import { UnsavedWorkGuardBoundary } from "./lib/useUnsavedWorkGuard";
import { useResolvedTheme } from "./design/theme";

// Wrap React.lazy so a failed dynamic import — almost always a STALE CHUNK (the open tab holds old
// chunk filenames after a dev rebuild or a prod redeploy) — auto-recovers with a single reload instead
// of dead-ending on the error boundary / a blank page. Loop-safe: reloads at most once per 10s (via a
// per-tab sessionStorage timestamp), so a genuinely broken build surfaces the real error instead of
// reload-looping. Non-chunk render errors are re-thrown untouched (the ErrorBoundary handles them).
const lazy = (f) =>
  React.lazy(async () => {
    try {
      return await f();
    } catch (err) {
      const isChunkErr =
        err?.name === "ChunkLoadError" ||
        /Loading chunk \d|Loading CSS chunk|dynamically imported module/i.test(err?.message || "");
      const KEY = "vp-chunk-reload-ts";
      let last = 0;
      try { last = Number(sessionStorage.getItem(KEY) || "0"); } catch { /* private mode */ }
      if (isChunkErr && Date.now() - last > 10000) {
        try { sessionStorage.setItem(KEY, String(Date.now())); } catch { /* ignore */ }
        window.location.reload();
        return await new Promise(() => {}); // hold Suspense until the reload takes over
      }
      throw err;
    }
  });
// Auth screens + the post-auth app chrome are lazy so the marketing "/" entry ships none of them.
// AppLayout transitively pulls GlobalSearch, AiraDrawer, recordRegistry, moduleArt (~the whole app
// shell) — it must never be a static import here.
const Login = lazy(() => import("./pages/Login"));
const ForcePasswordChange = lazy(() => import("./pages/ForcePasswordChange"));
const AppLayout = lazy(() => import("./components/AppLayout"));
const HrPayrollExperience = lazy(() => import("./components/hrPayroll/HrPayrollExperience"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Students = lazy(() => import("./pages/Students"));
const Admissions = lazy(() => import("./pages/Admissions"));
const Attendance = lazy(() => import("./pages/Attendance"));
const Timetable = lazy(() => import("./pages/Timetable"));
const Calendar = lazy(() => import("./pages/Calendar"));
const TeachingAssignments = lazy(() => import("./pages/TeachingAssignments"));
const Exams = lazy(() => import("./pages/Exams"));
const Fees = lazy(() => import("./pages/Fees"));
const Finance = lazy(() => import("./pages/Finance"));
const ReceiptBuilder = lazy(() => import("./pages/ReceiptBuilder"));
const FeeRecoveryAI = lazy(() => import("./pages/FeeRecoveryAI"));
const Tasks = lazy(() => import("./pages/Tasks"));
const HR = lazy(() => import("./pages/HR"));
const Organisation = lazy(() => import("./pages/Organisation"));
const CompensationWorkspace = lazy(() => import("./pages/CompensationWorkspace"));
const EmployeeFinanceWorkspace = lazy(() => import("./pages/EmployeeFinanceWorkspace"));
const EmployeeServiceWorkspace = lazy(() => import("./pages/EmployeeServiceWorkspace"));
const OffboardingWorkspace = lazy(() => import("./pages/OffboardingWorkspace"));
const WorkforceAnalyticsWorkspace = lazy(() => import("./pages/WorkforceAnalyticsWorkspace"));
const HrPayrollCopilot = lazy(() => import("./pages/HrPayrollCopilot"));
const HrPayrollAiInvocationPage = lazy(() => import("./pages/HrPayrollAiInvocationPage"));
const HrPayrollAiIncidentPage = lazy(() => import("./pages/HrPayrollAiIncidentPage"));
const HrPayrollAiRiskAssessmentPage = lazy(() => import("./pages/HrPayrollAiRiskAssessmentPage"));
const HrPayrollAiFairnessAssessmentPage = lazy(() => import("./pages/HrPayrollAiFairnessAssessmentPage"));
const HrPayrollAiFairnessDatasetPage = lazy(() => import("./pages/HrPayrollAiFairnessDatasetPage"));
const HrPayrollAiFeedbackPage = lazy(() => import("./pages/HrPayrollAiFeedbackPage"));
const HrPayrollAiPromptVersionPage = lazy(() => import("./pages/HrPayrollAiPromptVersionPage"));
const HrPayrollAiPromptReleaseRequestPage = lazy(() => import("./pages/HrPayrollAiPromptReleaseRequestPage"));
const HrPayrollAiAuditExportPage = lazy(() => import("./pages/HrPayrollAiAuditExportPage"));
const HrPayrollAiModelReleasePage = lazy(() => import("./pages/HrPayrollAiModelReleasePage"));
const HrPayrollAiDriftMonitorPage = lazy(() => import("./pages/HrPayrollAiDriftMonitorPage"));
const HrPayrollAiDriftRunPage = lazy(() => import("./pages/HrPayrollAiDriftRunPage"));
const PerformanceWorkspace = lazy(() => import("./pages/PerformanceWorkspace"));
const LearningDevelopmentWorkspace = lazy(() => import("./pages/LearningDevelopmentWorkspace"));
const Payroll = lazy(() => import("./pages/Payroll"));
const PayrollCompliance = lazy(() => import("./pages/PayrollCompliance"));
const HRRecruitment = lazy(() => import("./pages/HRRecruitment"));
const WorkforceOnboarding = lazy(() => import("./pages/WorkforceOnboarding"));
const HRCredentials = lazy(() => import("./pages/HRCredentials"));
const WorkforceTime = lazy(() => import("./pages/WorkforceTime"));
const WorkforceSchedules = lazy(() => import("./pages/WorkforceSchedules"));
const SubstituteCoverage = lazy(() => import("./pages/SubstituteCoverage"));
const WorkforceWorkload = lazy(() => import("./pages/WorkforceWorkload"));
const Transport = lazy(() => import("./pages/Transport"));
const Library = lazy(() => import("./pages/Library"));
const Hostel = lazy(() => import("./pages/Hostel"));
const Compliance = lazy(() => import("./pages/Compliance"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const Help = lazy(() => import("./pages/Help"));
const Contact = lazy(() => import("./pages/Contact"));
const AiraAssistant = lazy(() => import("./components/AiraAssistant"));
const CookieConsent = lazy(() => import("./components/CookieConsent"));

// The Vidyapeeth360 Aira Assistant loads on EVERY public MARKETING page (platform sales & support
// for guests). Landing mounts its own instance (its Request-a-Demo scrolls to the booking form);
// tenant sites keep the tenant AiraWidget; portals/app/auth/transactional flows get nothing here —
// signed-in users have the in-app Aira with live data access.
const MARKETING_AIRA_PATHS = [
  /^\/solutions(?:\/|$)/, /^\/help(\/|$)/, /^\/contact$/, /^\/about$/, /^\/security$/, /^\/privacy-policy$/,
  /^\/refund-policy$/, /^\/terms-and-conditions$/, /^\/cookie-policy$/, /^\/legal\//,
  /^\/videos$/, /^\/mobile-app$/, /^\/switch$/, /^\/pricing$/, /^\/compliance\//, /^\/modules/,
  /^\/integrations$/, /^\/implementation$/, /^\/school-chains$/, /^\/support$/, /^\/resources(\/|$)/,
  /^\/aira$/, // the extended Aira story page (WP-20) — its "Ask the live product assistant" CTA opens this FAB
];
export function normalizePublicPathname(pathname) {
  const value = typeof pathname === "string" && pathname ? pathname : "/";
  return value.length > 1 ? value.replace(/\/+$/, "") : value;
}
function MarketingAira({ enabled }) {
  const location = useLocation();
  const pathname = normalizePublicPathname(location.pathname);
  if (!enabled || !MARKETING_AIRA_PATHS.some((r) => r.test(pathname))) return null;
  return <AiraAssistant />;
}

// Cookie choices (necessary storage + opt-in public marketing analytics) — public PLATFORM
// marketing/auth surfaces only. Tenant white-label sites and the signed-in app do not mount this
// marketing preference UI, and analytics.js itself also fails closed without stored opt-in.
const COOKIE_NOTICE_PATHS = [
  /^\/$/, ...MARKETING_AIRA_PATHS, /^\/login$/, /^\/register$/,
  /^\/platform\/login$/, /^\/forgot-password$/, /^\/reset-password$/,
  /^\/account-deletion$/,
];
function CookieNotice({ enabled }) {
  const location = useLocation();
  const pathname = normalizePublicPathname(location.pathname);
  if (!enabled || !COOKIE_NOTICE_PATHS.some((r) => r.test(pathname))) return null;
  return <CookieConsent />;
}

// WP-06: consent-gated analytics for the public platform marketing surface, mounted ONCE at the
// router level. Fires an explicit page_view on every route change (this is an SPA — GA4's
// automatic page_view counts only the first document; see analytics.js send_page_view:false).
// Same path list as the cookie notice, so init/coverage and the consent UI can't drift apart.
function MarketingAnalyticsTracker({ enabled }) {
  const location = useLocation();
  const pathname = normalizePublicPathname(location.pathname);
  const onMarketingSurface = enabled && COOKIE_NOTICE_PATHS.some((r) => r.test(pathname));
  // Consent can be granted AFTER the route mounted (the canonical first-visit flow: land on "/",
  // accept the banner, convert without navigating). trackPageView fails closed pre-consent and
  // gtag runs send_page_view:false, so without this re-fire that whole session would have
  // conversion events but ZERO page_view. Bump a counter on every consent change so the
  // page_view effect re-evaluates; trackPageView itself stays consent-gated (no double count:
  // the pre-consent attempt was a no-op).
  const [consentVersion, setConsentVersion] = useState(0);
  useEffect(() => {
    const onConsent = () => setConsentVersion((v) => v + 1);
    window.addEventListener(COOKIE_CONSENT_EVENT, onConsent);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, onConsent);
  }, []);
  useEffect(() => {
    if (onMarketingSurface) initAnalytics();
  }, [onMarketingSurface]);
  // One page_view per PAGE, not per consent event: re-saving preferences (still granted) fires
  // COOKIE_CONSENT_EVENT again, and an unguarded re-fire would inflate page_view counts and
  // skew conversion denominators. The ref remembers which page already logged successfully.
  const lastTrackedRef = useRef(null);
  useEffect(() => {
    if (!onMarketingSurface) return;
    const page = pathname + location.search;
    if (lastTrackedRef.current === page) return;
    if (trackPageView(page) !== false) lastTrackedRef.current = page;
  }, [onMarketingSurface, pathname, location.search, consentVersion]);
  return null;
}
const WebsiteBuilder = lazy(() => import("./pages/WebsiteBuilder"));
const Payments = lazy(() => import("./pages/Payments"));
const PublicSite = lazy(() => import("./pages/PublicSite"));
const SolutionsHubPage = lazy(() => import("./pages/solutions/SolutionsHubPage"));
const SegmentPage = lazy(() => import("./pages/SegmentPage"));
// UX-03/PGS-4: the branded marketing 404 (noindex) — unknown public URLs render it instead of
// silently bouncing to the homepage.
const MarketingNotFound = lazy(() => import("./components/marketing/MarketingNotFound"));
const MobileAppShowcase = lazy(() => import("./pages/MobileAppShowcase"));
const Videos = lazy(() => import("./pages/Videos")); // legacy /videos → reviewed product-tour request

// The canonical solution registry owns the live route keys. Keeping route validation beside the
// page content prevents navigation, SEO and route support from drifting independently.
function SegmentRouteGate() {
  const { segment } = useParams();
  return SOLUTION_KEYS.includes(segment)
    ? <SegmentPage />
    : <MarketingNotFound />;
}
const SwitchPage = lazy(() => import("./pages/SwitchPage")); // /switch — switching & migration page
const IntegrationsPage = lazy(() => import("./pages/IntegrationsPage")); // /integrations — claims-registry-bounded ecosystem page (PGS-6/TRUST-7)
const ImplementationPage = lazy(() => import("./pages/ImplementationPage")); // /implementation — the first-90-days rollout story (PGS-7)
const SchoolChainsPage = lazy(() => import("./pages/SchoolChainsPage")); // /school-chains — the Enterprise group/trust capability (PGS-15)
const SupportPage = lazy(() => import("./pages/SupportPage")); // /support — support & onboarding process page (TRUST-8)
const ComplianceDpdp = lazy(() => import("./pages/ComplianceDpdp")); // /compliance/dpdp — public DPDP-for-schools page
const PricingPage = lazy(() => import("./pages/PricingPage")); // /pricing — standalone linkable pricing
const AiraMarketingPage = lazy(() => import("./pages/AiraMarketingPage")); // /aira — the extended Aira story (moved off the homepage, WP-20)
const ModulesHub = lazy(() => import("./pages/ModulesHub")); // /modules — the pictorial per-module directory
const ModuleBuyerGuidePage = lazy(() => import("./pages/ModuleBuyerGuidePage")); // /modules/guides/:guideKey — buyer-job guides over existing capabilities
const ModulePage = lazy(() => import("./pages/ModulePage")); // /modules/:key — one animated per-module page
const ResourcesIndexPage = lazy(() => import("./pages/ResourcesIndexPage")); // /resources — the public resource library index (SEO-12)
const ResourceArticlePage = lazy(() => import("./pages/ResourceArticlePage")); // /resources/:articleSlug — one prerendered resource article (SEO-12)
const AdmissionApply = lazy(() => import("./pages/AdmissionApply"));
const JoinPage = lazy(() => import("./pages/JoinPage"));
const RegistrationWizard = lazy(() => import("./pages/RegistrationWizard"));
const VerifyCertificate = lazy(() => import("./pages/VerifyCertificate"));
const RegistrationRequests = lazy(() => import("./pages/RegistrationRequests"));
const PTM = lazy(() => import("./pages/PTM"));
const Discipline = lazy(() => import("./pages/Discipline"));
const Achievements = lazy(() => import("./pages/Achievements"));
const FormsStudio = lazy(() => import("./pages/FormsStudio"));
const Grievances = lazy(() => import("./pages/Grievances"));
const MeetingNotes = lazy(() => import("./pages/MeetingNotes"));
const Support = lazy(() => import("./pages/Support"));
const ClassChat = lazy(() => import("./pages/ClassChat"));
const ChatModeration = lazy(() => import("./pages/ChatModeration"));
const DocumentsManager = lazy(() => import("./pages/DocumentsManager"));
const MyProfile = lazy(() => import("./pages/MyProfile"));
const ConsentCapture = lazy(() => import("./pages/ConsentCapture"));
const PlatformAdmin = lazy(() => import("./pages/PlatformAdmin"));
const PlatformAutomation = lazy(() => import("./pages/PlatformAutomation"));
const Communication = lazy(() => import("./pages/Communication"));
const StudentDetailPage = lazy(() => import("./pages/StudentDetailPage"));
const TeacherDashboard = lazy(() => import("./pages/TeacherDashboard"));
const TeacherDetail = lazy(() => import("./pages/TeacherDetail"));
const LeadDetailPage = lazy(() => import("./pages/LeadDetailPage"));
const InvoiceDetailPage = lazy(() => import("./pages/InvoiceDetailPage"));
const EmployeeDetailPage = lazy(() => import("./pages/EmployeeDetailPage"));
const EmploymentAgreementDetailPage = lazy(() => import("./pages/EmploymentAgreementDetailPage"));
const HrPayrollRecordDetailPage = lazy(() => import("./pages/HrPayrollRecordDetailPage"));
const RouteDetailPage = lazy(() => import("./pages/RouteDetailPage"));
const TransportDriverDetailPage = lazy(() => import("./pages/TransportDriverDetailPage"));
const BookDetailPage = lazy(() => import("./pages/BookDetailPage"));
const RoomDetailPage = lazy(() => import("./pages/RoomDetailPage"));
const ExamDetailPage = lazy(() => import("./pages/ExamDetailPage"));
const OnlineExamDetailPage = lazy(() => import("./pages/OnlineExamDetailPage"));
const ReportCardDetailPage = lazy(() => import("./pages/ReportCardDetailPage"));
const ScholarshipApplicationDetailPage = lazy(() => import("./pages/ScholarshipApplicationDetailPage"));
const CertificateDetailPage = lazy(() => import("./pages/CertificateDetailPage"));
const HealthDetailPage = lazy(() => import("./pages/HealthDetailPage"));
const LeaveDetailPage = lazy(() => import("./pages/LeaveDetailPage"));
const ItemDetailPage = lazy(() => import("./pages/ItemDetailPage"));
const TaskDetailPage = lazy(() => import("./pages/TaskDetailPage"));
const TaskScanLanding = lazy(() => import("./pages/tasks/ScanLanding"));
const CourseDetailPage = lazy(() => import("./pages/CourseDetailPage"));
const AlumniDetailPage = lazy(() => import("./pages/AlumniDetailPage"));
const AnnouncementDetailPage = lazy(() => import("./pages/AnnouncementDetailPage"));
const ExpenseDetailPage = lazy(() => import("./pages/ExpenseDetailPage"));
const VisitorDetailPage = lazy(() => import("./pages/VisitorDetailPage"));
const BusTripDetailPage = lazy(() => import("./pages/BusTripDetailPage"));
// Parity modules (gaps #2-10)
const Certificates = lazy(() => import("./pages/Certificates"));
const CertificateBuilder = lazy(() => import("./pages/CertificateBuilder"));
const Homework = lazy(() => import("./pages/Homework"));
const LessonPlans = lazy(() => import("./pages/LessonPlans"));
const Leave = lazy(() => import("./pages/Leave"));
const OnlineExams = lazy(() => import("./pages/OnlineExams"));
const ReportCards = lazy(() => import("./pages/ReportCards"));
const ReportCardStudio = lazy(() => import("./pages/ReportCardStudio"));
const Insights = lazy(() => import("./pages/Insights"));
const GroupConsole = lazy(() => import("./pages/GroupConsole"));
const Inventory = lazy(() => import("./pages/Inventory"));
// Parity modules (gaps #11-20)
const FrontOffice = lazy(() => import("./pages/FrontOffice"));
const Health = lazy(() => import("./pages/Health"));
const Biometric = lazy(() => import("./pages/Biometric"));
const DigitalClassroom = lazy(() => import("./pages/DigitalClassroom"));
const Reports = lazy(() => import("./pages/Reports"));
const Alumni = lazy(() => import("./pages/Alumni"));
const Scholarships = lazy(() => import("./pages/Scholarships"));
const Accounting = lazy(() => import("./pages/Accounting"));
const Branding = lazy(() => import("./pages/Branding"));
const BusTracking = lazy(() => import("./pages/BusTracking"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Automation = lazy(() => import("./pages/Automation"));
const AutomationBuilder = lazy(() => import("./pages/AutomationBuilder"));
const Migration = lazy(() => import("./pages/Migration"));
const Inbox = lazy(() => import("./pages/Inbox"));
const Assistant = lazy(() => import("./pages/Assistant"));
const Settings = lazy(() => import("./pages/Settings"));
const LoginHistory = lazy(() => import("./pages/LoginHistory"));
const EmailTemplates = lazy(() => import("./pages/EmailTemplates"));
const Billing = lazy(() => import("./pages/Billing"));
const BillingReturn = lazy(() => import("./pages/BillingReturn"));
const PayNow = lazy(() => import("./pages/PayNow"));
const CandidateOfferPage = lazy(() => import("./pages/CandidateOfferPage"));
const PublicCareersPage = lazy(() => import("./pages/PublicCareersPage"));
const ParentLogin = lazy(() => import("./pages/parent/ParentLogin"));
const ParentPortal = lazy(() => import("./pages/parent/ParentPortal"));
const StudentLogin = lazy(() => import("./pages/student/StudentLogin"));
const StudentPortal = lazy(() => import("./pages/student/StudentPortal"));
const Design = lazy(() => import("./pages/Design")); // internal: design-system v2 gallery (/design)
const Chrome = lazy(() => import("./pages/Chrome")); // internal: per-persona app chrome (/chrome)
const LegalPage = lazy(() => import("./pages/legal/LegalPage")); // About / Privacy / Refund / Terms
const Security = lazy(() => import("./pages/Security")); // /security — public trust & compliance page
const AccountDeletion = lazy(() => import("./pages/AccountDeletion")); // public Apple/Google account-deletion route

function PermissionRestrictedState({ perm, anyPerms, accessLabel, deniedTo, pathname }) {
  const required = accessLabel
    || perm
    || (anyPerms?.length ? anyPerms.join(" or ") : "an additional role capability");
  return (
    <div className="mx-auto w-full max-w-3xl py-8" data-testid="permission-restricted-state">
      <Card>
        <EmptyState
          icon={ShieldAlert}
          title="Access restricted"
          desc={`Your current role cannot open ${pathname}. Ask a tenant administrator for ${required}.`}
          action={(
            <Link
              to={deniedTo}
              className="inline-flex min-h-11 items-center justify-center rounded-xl bg-primary px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
            >
              Open an available workspace
            </Link>
          )}
        />
        <div className="mx-5 mb-5 rounded-xl border border-line bg-surface-subtle px-4 py-3 text-xs text-ink-2 sm:mx-6 sm:mb-6">
          <span className="font-semibold text-ink">Required access: </span>
          <code className="font-mono">{required}</code>. The blocked page and its APIs remain
          permission protected; no data was loaded.
        </div>
      </Card>
    </div>
  );
}

const ENTITLEMENT_LABELS = {
  hr_payroll: "HR & Payroll",
  workforce_analytics: "Workforce Analytics",
  assistant: "Aira Assistant",
  hr_payroll_ai: "HR & Payroll Intelligence",
};

function EntitlementRestrictedState({ missingFeatures, pathname }) {
  const primary = missingFeatures[0] || "hr_payroll";
  const capability = ENTITLEMENT_LABELS[primary] || primary;
  return (
    <section
      className="mx-auto w-full max-w-3xl py-8 sm:py-12"
      aria-label={`${capability} access restricted`}
      data-testid="entitlement-restricted-state"
    >
      <Card className="overflow-hidden border-primary/15 shadow-elev-2">
        <div className="border-b border-line bg-gradient-to-br from-primary-50/90 via-surface to-ai-soft/35 px-5 py-4 sm:px-6">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-accent">
            Platform-controlled access
          </p>
        </div>
        <EmptyState
          icon={ShieldAlert}
          title={`${capability} is not enabled`}
          desc={`This capability is held behind a platform launch gate for your school. A platform administrator can enable it when release approval is complete.`}
          action={(
            <Link
              to="/app/dashboard"
              className="inline-flex min-h-11 items-center justify-center rounded-xl bg-primary px-4 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
            >
              Return to dashboard
            </Link>
          )}
        />
        <div className="mx-5 mb-5 rounded-xl border border-line bg-surface-subtle px-4 py-3 text-xs leading-5 text-ink-2 sm:mx-6 sm:mb-6">
          <p>
            <span className="font-semibold text-ink">Requested page: </span>
            <code className="break-all font-mono">{pathname}</code>
          </p>
          <p className="mt-1">
            <span className="font-semibold text-ink">Enable before access: </span>
            {missingFeatures.map((feature) => (
              <code key={feature} className="mr-1.5 inline-block font-mono">{feature}</code>
            ))}
          </p>
          <p className="mt-1">The protected workspace was not mounted, so its data was not loaded.</p>
        </div>
      </Card>
    </section>
  );
}

export function Protected({
  children,
  perm,
  anyPerms,
  accessCheck,
  accessLabel,
  deniedTo = "/app/dashboard",
}) {
  const { user, loading, can } = useAuth();
  const location = useLocation();
  if (loading || user === null)
    return <div className="min-h-screen grid place-items-center bg-muted"><Spinner className="w-8 h-8" /></div>;
  if (!user) {
    // Preserve the intended deep link through login (?next=…) — Login.js honors safe internal paths.
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  // First-login gate: an admin-onboarded staff with a temporary password must set their own before
  // reaching any app screen (the backend clears must_change_password on /auth/change-password).
  if (user.must_change_password) return <ForcePasswordChange />;
  if (isTenantlessPlatformUser(user))
    return <Navigate to="/platform" replace />;
  // Platform-controlled HR & Payroll launch gates are evaluated before RBAC and before the route
  // child is rendered. They deliberately fail closed when an older session has no feature list.
  const requiredFeatures = requiredHrPayrollEntitlements(location);
  if (!hasRequiredSessionFeatures(user, requiredFeatures)) {
    const missingFeatures = requiredFeatures.filter(
      (feature) => !Array.isArray(user.features) || !user.features.includes(feature),
    );
    return (
      <AppLayout>
        <EntitlementRestrictedState
          missingFeatures={missingFeatures}
          pathname={`${location.pathname}${location.search}`}
        />
      </AppLayout>
    );
  }
  // Route-level RBAC guard: mirrors the sidebar's can(perm) gating so deep-linking to a module the
  // user lacks access to (e.g. a teacher opening /app/fees) is answered IN PLACE by the governed
  // restricted state — which names the missing grant and offers a workspace they can open — rather
  // than a broken "Failed to load" shell or a silent bounce that never says what was missing.
  // Fail-closed is unchanged: this returns before `children`, so the blocked page never mounts and
  // its APIs are never called.
  if (
    (perm && !can(perm)) ||
    (anyPerms?.length && !anyPerms.some(can)) ||
    (accessCheck && !accessCheck(can))
  ) {
    return (
      <AppLayout>
        <BillingGate>
          <PermissionRestrictedState
            perm={perm}
            anyPerms={anyPerms}
            accessLabel={accessLabel}
            deniedTo={deniedTo}
            pathname={location.pathname}
          />
        </BillingGate>
      </AppLayout>
    );
  }
  const content = isHrPayrollExperiencePath(location.pathname)
    ? <HrPayrollExperience>{children}</HrPayrollExperience>
    : children;
  return <AppLayout><BillingGate>{content}</BillingGate></AppLayout>;
}

export function EmployeeServiceAdminRoute({ children }) {
  return (
    <Protected
      accessCheck={hasEmployeeServiceWorkspaceAccess}
      deniedTo="/app/me?section=employee-service"
    >
      {children}
    </Protected>
  );
}

export function LearningDevelopmentAdminRoute({ children }) {
  return (
    <Protected
      accessCheck={hasLearningDevelopmentWorkspaceAccess}
      deniedTo="/app/me?section=learning"
    >
      {children}
    </Protected>
  );
}

export function PerformanceAdminRoute({ children }) {
  return (
    <Protected
      accessCheck={hasPerformanceWorkspaceAccess}
      deniedTo="/app/me?section=performance"
    >
      {children}
    </Protected>
  );
}

function PlatformCapabilityBoundary({ children, capability }) {
  const { status, error, retry, hasCapability } = usePlatformAccess();
  const { logout } = useAuth();
  const navigate = useNavigate();

  if (status === "loading") return <PlatformAccessState kind="loading" />;
  if (status === "error") {
    return (
      <PlatformAccessState
        kind={error?.kind === "denied" ? "denied" : "unavailable"}
        onRetry={retry}
        onSignOut={logout}
      />
    );
  }
  if (capability && !hasCapability(capability)) {
    return (
      <PlatformAccessState
        kind="denied"
        detail="Your current platform role does not include this capability. No data from this area was loaded."
        onBack={() => navigate("/platform", { replace: true })}
        onSignOut={logout}
      />
    );
  }
  return children;
}

function PlatformProtected({ children, capability, accessHandledByChild = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null)
    return <div className="min-h-screen grid place-items-center bg-muted"><Spinner className="w-8 h-8" /></div>;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/platform/login?next=${next}`} replace />;
  }
  if (user.must_change_password) return <ForcePasswordChange />;
  if (!isTenantlessPlatformUser(user))
    return <Navigate to="/app/dashboard" replace />;
  return (
    <PlatformAccessProvider>
      {accessHandledByChild ? children : (
        <PlatformCapabilityBoundary capability={capability}>{children}</PlatformCapabilityBoundary>
      )}
    </PlatformAccessProvider>
  );
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center bg-muted"><Spinner className="w-8 h-8" /></div>;
  if (user) return <Navigate to={resolveHomeRoute(user)} replace />;
  return children;
}

const FullSpinner = () => (
  <div className="min-h-screen grid place-items-center bg-muted"><Spinner className="w-8 h-8" /></div>
);

function ParentRequireAuth({ children }) {
  const { parent, loading } = useParentAuth();
  if (loading || parent === null) return <FullSpinner />;
  if (!parent) return <Navigate to="/parent/login" replace />;
  return children;
}

function ParentPublicOnly({ children }) {
  const { parent, loading } = useParentAuth();
  if (loading) return <FullSpinner />;
  if (parent) return <Navigate to="/parent/dashboard" replace />;
  return children;
}

function ParentArea() {
  return (
    <ParentAuthProvider>
      <Routes>
        <Route path="login" element={<ParentPublicOnly><ParentLogin /></ParentPublicOnly>} />
        <Route path="dashboard" element={<ParentRequireAuth><ParentPortal /></ParentRequireAuth>} />
        <Route path="*" element={<Navigate to="/parent/dashboard" replace />} />
      </Routes>
    </ParentAuthProvider>
  );
}

function StudentRequireAuth({ children }) {
  const { student, loading } = useStudentAuth();
  if (loading || student === null) return <FullSpinner />;
  if (!student) return <Navigate to="/student/login" replace />;
  return children;
}

function StudentPublicOnly({ children }) {
  const { student, loading } = useStudentAuth();
  if (loading) return <FullSpinner />;
  if (student) return <Navigate to="/student/dashboard" replace />;
  return children;
}

function StudentArea() {
  return (
    <StudentAuthProvider>
      <Routes>
        <Route path="login" element={<StudentPublicOnly><StudentLogin /></StudentPublicOnly>} />
        <Route path="dashboard" element={<StudentRequireAuth><StudentPortal /></StudentRequireAuth>} />
        <Route path="*" element={<Navigate to="/student/dashboard" replace />} />
      </Routes>
    </StudentAuthProvider>
  );
}

function AppRouter() {
  const location = useLocation();
  // Early return renders OUTSIDE the router-level <Suspense> below — a lazy component needs its
  // own boundary here or React throws on the OAuth-callback path.
  if (location.hash?.includes("session_id="))
    return <React.Suspense fallback={<FullSpinner />}><AuthCallback /></React.Suspense>;

  // What renders at "/" depends on the host (hostname -> tenant resolution):
  //  • apex marketing (vidyapeeth360.com / www), reserved subdomains, the app/api host, and
  //    dev/preview (localhost, *.github.dev, Firebase) -> the COMPANY marketing page (<Landing/>).
  //  • a real tenant subdomain (<slug>.vidyapeeth360.com, via getTenantSlug) OR a connected external
  //    school domain (e.g. stmarys.edu) -> that tenant's published site (<PublicSite domainMode/>).
  const ROOT_DOMAIN = "vidyapeeth360.com";
  let appHost = "";
  try { appHost = new URL(process.env.REACT_APP_BACKEND_URL).hostname.toLowerCase(); } catch { /* noop */ }
  const here = (window.location.hostname || "").toLowerCase();
  const isExternalDomain = !!here
    && !["localhost", "127.0.0.1", "0.0.0.0"].includes(here)
    && here !== ROOT_DOMAIN && !here.endsWith(`.${ROOT_DOMAIN}`)
    && !here.endsWith(".github.dev") && !here.endsWith(".web.app") && !here.endsWith(".firebaseapp.com")
    // dev/preview tunnels (Cloudflare / ngrok / localtunnel / localhost.run) are NOT tenant sites
    && !here.endsWith(".trycloudflare.com") && !here.endsWith(".ngrok-free.app") && !here.endsWith(".ngrok.io")
    && !here.endsWith(".loca.lt") && !here.endsWith(".lhr.life")
    && !(appHost && here === appHost);
  const isTenantSite = !!getTenantSlug() || isExternalDomain;
  // Standard SaaS host split (founder decision 2026-08-23): marketing lives on the apex/www,
  // the PRODUCT lives on app.<domain> — like slack.com vs app.slack.com. On the app host the
  // root is never the marketing page: it goes straight to sign-in (PublicOnly then forwards an
  // already-authenticated user to their home route). Only "/" changes — every other path
  // (deep links, /pay, /platform/login, marketing routes someone bookmarked) stays reachable.
  const isAppProductHost = here === `app.${ROOT_DOMAIN}`;
  const isSensitivePublicRoute =
    location.pathname === "/account-deletion"
    || location.pathname.startsWith("/careers");

  return (
    <React.Suspense fallback={<FullSpinner />}>
    <Routes>
      <Route path="/" element={isAppProductHost ? <Navigate to="/login" replace /> : isTenantSite ? <PublicSite domainMode /> : <Landing />} />
      <Route path="/solutions" element={<SolutionsHubPage />} />
      <Route path="/solutions/:segment" element={<SegmentRouteGate />} />
      <Route path="/mobile-app" element={<MobileAppShowcase />} />
      <Route path="/videos" element={<Videos />} />
      <Route path="/switch" element={<SwitchPage />} />
      <Route path="/integrations" element={<IntegrationsPage />} />
      <Route path="/implementation" element={<ImplementationPage />} />
      <Route path="/school-chains" element={<SchoolChainsPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/aira" element={<AiraMarketingPage />} />
      <Route path="/modules" element={<ModulesHub />} />
      <Route path="/modules/guides/:guideKey" element={<ModuleBuyerGuidePage />} />
      <Route path="/modules/:key" element={<ModulePage />} />
      {/* SEO-12 — the resource library. Path-based article URLs are prerendered and sitemapped;
          an unknown slug renders the marketing 404 (noindex) rather than a soft-200 redirect. */}
      <Route path="/resources" element={<ResourcesIndexPage />} />
      <Route path="/resources/:articleSlug" element={<ResourceArticlePage />} />
      <Route path="/apply/:slug" element={<AdmissionApply />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/platform/login" element={<PublicOnly><Login audience="platform" /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/pay/:token" element={<PayNow />} />
      <Route path="/candidate-offer/:token" element={<CandidateOfferPage />} />
      <Route path="/careers" element={<PublicCareersPage />} />
      <Route path="/careers/jobs/:jobSlug" element={<PublicCareersPage />} />
      <Route path="/careers/:tenantSlug" element={<PublicCareersPage />} />
      <Route path="/careers/:tenantSlug/jobs/:jobSlug" element={<PublicCareersPage />} />
      {/* ONB-10: the shareable per-school join page (School Code / QR target) */}
      <Route path="/join/:slug" element={<JoinPage />} />
      {/* Public self-registration wizard + status/set-password (?rid=) */}
      <Route path="/join/:slug/registration" element={<RegistrationWizard />} />
      {/* splat on the serial — certificate serials contain slashes (e.g. TC/2025-26/0001) */}
      <Route path="/verify/:slug/*" element={<VerifyCertificate />} />
      <Route path="/site/:slug" element={<PublicSite />} />
      <Route path="/consent/:token" element={<ConsentCapture />} />
      <Route path="/help" element={<Help />} />
      {/* PGS-5/SEO-3 — crawlable per-article URLs; /help?article=<slug> client-redirects here. */}
      <Route path="/help/articles/:articleSlug" element={<Help />} />
      <Route path="/contact" element={<Contact />} />
      {/* Company / legal pages — content is DB-backed + platform-admin editable (served at /api/public/legal/:slug) */}
      <Route path="/about" element={<LegalPage slug="about" />} />
      {/* Trust & security page — code-authored (claims are grounded in the shipped product, not CMS copy) */}
      <Route path="/security" element={<Security />} />
      {/* DPDP-for-schools page — code-authored, grounded in the shipped DPDP machinery (never legal advice) */}
      <Route path="/compliance/dpdp" element={<ComplianceDpdp />} />
      <Route path="/account-deletion" element={<AccountDeletion />} />
      <Route path="/privacy-policy" element={<LegalPage slug="privacy-policy" />} />
      <Route path="/refund-policy" element={<LegalPage slug="refund-policy" />} />
      <Route path="/terms-and-conditions" element={<LegalPage slug="terms-and-conditions" />} />
      <Route path="/cookie-policy" element={<LegalPage slug="cookie-policy" />} />
      {/* Any other legal slug the platform admin publishes resolves dynamically */}
      <Route path="/legal/:slug" element={<LegalPage />} />
      {/* SEC-9 — the internal visual-QA galleries are BUILD-TIME gated: on in dev/e2e, off in a
          real production build unless REACT_APP_ENABLE_INTERNAL_GALLERIES=true is set at build
          time (CRA inlines both env reads, so terser drops the routes AND their chunks from a
          prod bundle). Gated-off, the paths fall through to the marketing 404 — aligning these
          galleries with the CLAUDE.md "no dev affordance reachable in production" rail. */}
      {(process.env.NODE_ENV !== "production" || process.env.REACT_APP_ENABLE_INTERNAL_GALLERIES === "true") && (
        <Route
          path="/design"
          element={<InternalNoIndex path="/design" title="Vidyapeeth360 Design System — Internal"><Design /></InternalNoIndex>}
        />
      )}
      {(process.env.NODE_ENV !== "production" || process.env.REACT_APP_ENABLE_INTERNAL_GALLERIES === "true") && (
        <Route
          path="/chrome"
          element={<InternalNoIndex path="/chrome" title="Vidyapeeth360 App Chrome — Internal"><Chrome /></InternalNoIndex>}
        />
      )}
      <Route path="/platform" element={<PlatformProtected accessHandledByChild><PlatformAdmin /></PlatformProtected>} />
      <Route path="/platform/tenants/:tenantId/360" element={<PlatformProtected capability="tenants.read"><PlatformAdmin /></PlatformProtected>} />
      <Route path="/platform/support/tickets/:ticketId" element={<PlatformProtected capability="service.read"><PlatformAdmin /></PlatformProtected>} />
      <Route path="/platform/automation" element={<PlatformProtected capability="operations.read"><PlatformAutomation /></PlatformProtected>} />
      <Route path="/platform/automation/build" element={<PlatformProtected capability="operations.write"><AutomationBuilder scope="platform" /></PlatformProtected>} />
      <Route path="/platform/automation/build/:id" element={<PlatformProtected capability="operations.write"><AutomationBuilder scope="platform" /></PlatformProtected>} />
      <Route path="/parent/*" element={<ParentArea />} />
      <Route path="/student/*" element={<StudentArea />} />
      <Route path="/app/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/app/students" element={<Protected perm="students:read"><Students /></Protected>} />
      <Route path="/app/students/:id" element={<Protected perm="students:read"><StudentDetailPage /></Protected>} />
      <Route path="/app/admissions" element={<Protected perm="admissions:read"><Admissions /></Protected>} />
      <Route path="/app/registrations" element={<Protected perm="registration:read"><RegistrationRequests /></Protected>} />
      <Route path="/app/admissions/:id" element={<Protected perm="admissions:read"><LeadDetailPage /></Protected>} />
      <Route path="/app/attendance" element={<Protected perm="attendance:read"><Attendance /></Protected>} />
      <Route path="/app/calendar" element={<Protected perm="communication:read"><Calendar /></Protected>} />
      <Route path="/app/ptm" element={<Protected perm="ptm:read"><PTM /></Protected>} />
      <Route path="/app/discipline" element={<Protected perm="discipline:read"><Discipline /></Protected>} />
      <Route path="/app/achievements" element={<Protected perm="achievements:read"><Achievements /></Protected>} />
      <Route path="/app/forms" element={<Protected perm="forms:read"><FormsStudio /></Protected>} />
      <Route path="/app/grievances" element={<Protected perm="grievance:read"><Grievances /></Protected>} />
      <Route path="/app/meeting-notes" element={<Protected perm="meeting_notes:read"><MeetingNotes /></Protected>} />
          <Route path="/app/support" element={<Protected perm="support:read"><Support /></Protected>} />
      <Route path="/app/documents" element={<Protected perm="students:read"><DocumentsManager /></Protected>} />
      <Route path="/app/timetable" element={<Protected perm="timetable:read"><Timetable /></Protected>} />
      <Route path="/app/teaching-assignments" element={<Protected perm="timetable:update"><TeachingAssignments /></Protected>} />
      <Route path="/app/exams" element={<Protected perm="exams:read"><Exams /></Protected>} />
      <Route path="/app/certificates" element={<Protected perm="certificates:read"><Certificates /></Protected>} />
      <Route path="/app/certificates/builder" element={<Protected perm="certificates:read"><CertificateBuilder /></Protected>} />
      <Route path="/app/homework" element={<Protected perm="homework:read"><Homework /></Protected>} />
      <Route path="/app/lessonplan" element={<Protected perm="lessonplan:read"><LessonPlans /></Protected>} />
      <Route path="/app/onlineexam" element={<Protected perm="onlineexam:read"><OnlineExams /></Protected>} />
      <Route path="/app/reportcards" element={<Protected perm="reportcards:read"><ReportCards /></Protected>} />
      <Route path="/app/reportcards/studio" element={<Protected perm="reportcards:read"><ReportCardStudio /></Protected>} />
      {/* legacy designer route → folded into the Studio's Layout tab */}
      <Route path="/app/reportcards/designer" element={<Navigate to="/app/reportcards/studio?tab=layout" replace />} />
      <Route path="/app/insights" element={<Protected perm="insights:read"><Insights /></Protected>} />
      <Route path="/app/fee-recovery-ai" element={<Protected perm="fees:read"><FeeRecoveryAI /></Protected>} />
      <Route path="/app/group" element={<Protected perm="group:read"><GroupConsole /></Protected>} />
      <Route path="/app/leave" element={<Protected perm="leave:read"><Leave /></Protected>} />
      <Route path="/app/inventory" element={<Protected perm="inventory:read"><Inventory /></Protected>} />
      <Route path="/app/reports" element={<Protected perm="reports:read"><Reports /></Protected>} />
      <Route path="/app/frontoffice" element={<Protected perm="frontoffice:read"><FrontOffice /></Protected>} />
      <Route path="/app/health" element={<Protected perm="health:read"><Health /></Protected>} />
      <Route path="/app/biometric" element={<Protected perm="biometric:read"><Biometric /></Protected>} />
      <Route path="/app/lms" element={<Protected perm="lms:read"><DigitalClassroom /></Protected>} />
      <Route path="/app/alumni" element={<Protected perm="alumni:read"><Alumni /></Protected>} />
      <Route path="/app/scholarship" element={<Protected perm="scholarship:read"><Scholarships /></Protected>} />
      <Route path="/app/accounting" element={<Protected perm="accounting:read"><Accounting /></Protected>} />
      <Route path="/app/branding" element={<Protected perm="whitelabel:read"><Branding /></Protected>} />
      <Route path="/app/email-templates" element={<Protected perm="settings:configure"><EmailTemplates /></Protected>} />
      <Route path="/app/email-templates/:templateId" element={<Protected perm="settings:configure"><EmailTemplates /></Protected>} />
      <Route path="/app/bustracking" element={<Protected perm="bustracking:read"><BusTracking /></Protected>} />
      <Route path="/app/onboarding" element={<Protected perm="onboarding:read"><Onboarding /></Protected>} />
      <Route path="/app/automation" element={<Protected perm="automation:read"><Automation /></Protected>} />
      <Route path="/app/automation/build" element={<Protected perm="automation:read"><AutomationBuilder /></Protected>} />
      <Route path="/app/automation/build/:id" element={<Protected perm="automation:read"><AutomationBuilder /></Protected>} />
      <Route path="/app/migration" element={<Protected perm="migration:read"><Migration /></Protected>} />
      <Route path="/app/finance" element={<Protected perm="fees:read"><Finance /></Protected>} />
      <Route path="/app/fees" element={<Protected perm="fees:read"><Fees /></Protected>} />
      <Route path="/app/fees/receipt-builder" element={<Protected perm="fees:configure"><ReceiptBuilder /></Protected>} />
      <Route path="/app/fees/invoices/:id" element={<Protected perm="fees:read"><InvoiceDetailPage /></Protected>} />
      <Route path="/app/tasks" element={<Protected perm="tasks:read"><Tasks /></Protected>} />
      {/* ONB-15: staff self-service — any signed-in staff member (no module perm needed) */}
      <Route path="/app/me" element={<Protected><MyProfile /></Protected>} />
      <Route path="/app/hr" element={<Protected perm="hr:read"><HR /></Protected>} />
      <Route path="/app/hr/recruitment" element={<Protected perm="recruitment:read"><HRRecruitment /></Protected>} />
      <Route path="/app/hr/onboarding" element={<Protected perm="hr_onboarding:read"><WorkforceOnboarding /></Protected>} />
      <Route path="/app/hr/credentials" element={<Protected perm="credentials:read"><HRCredentials /></Protected>} />
      <Route path="/app/hr/workforce-time" element={<Protected perm="workforce_time:read"><WorkforceTime /></Protected>} />
      <Route path="/app/hr/workforce-schedules" element={<Protected perm="workforce_scheduling:read"><WorkforceSchedules /></Protected>} />
      <Route path="/app/hr/substitute-coverage" element={<Protected perm="substitute_coverage:read"><SubstituteCoverage /></Protected>} />
      <Route path="/app/hr/workforce-workload" element={<Protected perm="workforce_workload:read"><WorkforceWorkload /></Protected>} />
      <Route path="/app/hr/compensation" element={<Protected perm="compensation:read"><CompensationWorkspace /></Protected>} />
      <Route path="/app/hr/employee-finance" element={<Protected perm="employee_finance:read"><EmployeeFinanceWorkspace /></Protected>} />
      <Route path="/app/hr/employee-service" element={<EmployeeServiceAdminRoute><EmployeeServiceWorkspace /></EmployeeServiceAdminRoute>} />
      <Route path="/app/hr/offboarding" element={<Protected perm="offboarding:read"><OffboardingWorkspace /></Protected>} />
      <Route path="/app/hr/analytics" element={<Protected perm="workforce_analytics:read"><WorkforceAnalyticsWorkspace /></Protected>} />
      <Route path="/app/hr/copilot" element={<Protected perm="hr_payroll_ai:read"><HrPayrollCopilot /></Protected>} />
      <Route path="/app/hr/copilot/invocations/:invocationId" element={<Protected perm="hr_payroll_ai:read"><HrPayrollAiInvocationPage /></Protected>} />
      <Route path="/app/hr/copilot/incidents/:incidentId" element={<Protected perm="hr_payroll_ai:configure"><HrPayrollAiIncidentPage /></Protected>} />
      <Route path="/app/hr/copilot/risk-assessments/:assessmentId" element={<Protected perm="hr_payroll_ai:configure"><HrPayrollAiRiskAssessmentPage /></Protected>} />
      <Route path="/app/hr/copilot/fairness-assessments/:assessmentId" element={<Protected perm="hr_payroll_ai:configure" accessCheck={(can) => can("workforce_analytics:read")} accessLabel="hr_payroll_ai:configure and workforce_analytics:read"><HrPayrollAiFairnessAssessmentPage /></Protected>} />
      <Route path="/app/hr/copilot/fairness-datasets/:datasetId" element={<Protected perm="hr_payroll_ai:configure" accessCheck={(can) => can("workforce_analytics:read")} accessLabel="hr_payroll_ai:configure and workforce_analytics:read"><HrPayrollAiFairnessDatasetPage /></Protected>} />
      <Route path="/app/hr/copilot/feedback/:caseId" element={<Protected perm="hr_payroll_ai:configure"><HrPayrollAiFeedbackPage /></Protected>} />
      <Route path="/app/hr/copilot/prompts/:promptVersionId" element={<Protected perm="hr_payroll_ai:configure"><HrPayrollAiPromptVersionPage /></Protected>} />
      <Route path="/app/hr/copilot/prompt-releases/:releaseRequestId" element={<Protected perm="hr_payroll_ai:configure"><HrPayrollAiPromptReleaseRequestPage /></Protected>} />
      <Route path="/app/hr/copilot/audit-exports/:exportId" element={<Protected perm="hr_payroll_ai:configure" accessCheck={(can) => can("workforce_analytics:export")}><HrPayrollAiAuditExportPage /></Protected>} />
      <Route path="/app/hr/copilot/model-releases/:releaseId" element={<Protected perm="hr_payroll_ai:configure"><HrPayrollAiModelReleasePage /></Protected>} />
      <Route path="/app/hr/copilot/drift-monitors/:monitorId" element={<Protected perm="hr_payroll_ai:configure"><HrPayrollAiDriftMonitorPage /></Protected>} />
      <Route path="/app/hr/copilot/drift-runs/:runId" element={<Protected perm="hr_payroll_ai:configure"><HrPayrollAiDriftRunPage /></Protected>} />
      <Route path="/app/hr/performance" element={<PerformanceAdminRoute><PerformanceWorkspace /></PerformanceAdminRoute>} />
      <Route path="/app/hr/learning-development" element={<LearningDevelopmentAdminRoute><LearningDevelopmentWorkspace /></LearningDevelopmentAdminRoute>} />
      <Route path="/app/organisation" element={<Protected perm="organization:read"><Organisation /></Protected>} />
      <Route path="/app/payroll" element={<Protected perm="payroll:read"><Payroll /></Protected>} />
      <Route path="/app/payroll/compliance" element={<Protected perm="statutory:read"><PayrollCompliance /></Protected>} />
      <Route path="/app/payroll/:runId" element={<Protected perm="payroll:read"><Payroll /></Protected>} />
      <Route path="/app/hr/employees/:id" element={<Protected perm="hr:read"><EmployeeDetailPage /></Protected>} />
      <Route path="/app/hr/employment-agreements/:agreementId" element={<Protected perm="employment_contracts:read"><EmploymentAgreementDetailPage /></Protected>} />
      <Route
        path="/app/hr/records/:domain/:id"
        element={(
          <Protected>
            <HrPayrollRecordDetailPage />
          </Protected>
        )}
      />
      <Route path="/app/teachers" element={<Protected perm="hr:read"><TeacherDashboard /></Protected>} />
      <Route path="/app/teachers/:id" element={<Protected perm="hr:read"><TeacherDetail /></Protected>} />
      <Route path="/app/transport/routes/:id" element={<Protected perm="transport:read"><RouteDetailPage /></Protected>} />
      <Route path="/app/transport/drivers/:id" element={<Protected perm="transport:read"><TransportDriverDetailPage /></Protected>} />
      <Route path="/app/library/books/:id" element={<Protected perm="library:read"><BookDetailPage /></Protected>} />
      <Route path="/app/hostel/rooms/:id" element={<Protected perm="hostel:read"><RoomDetailPage /></Protected>} />
      <Route path="/app/exams/:id" element={<Protected perm="exams:read"><ExamDetailPage /></Protected>} />
      <Route path="/app/onlineexam/:id" element={<Protected perm="onlineexam:read"><OnlineExamDetailPage /></Protected>} />
      <Route path="/app/reportcards/:id" element={<Protected perm="reportcards:read"><ReportCardDetailPage /></Protected>} />
      <Route path="/app/scholarship/applications/:id" element={<Protected perm="scholarship:read"><ScholarshipApplicationDetailPage /></Protected>} />
      <Route path="/app/certificates/:id" element={<Protected perm="certificates:read"><CertificateDetailPage /></Protected>} />
      <Route path="/app/health/:id" element={<Protected perm="health:read"><HealthDetailPage /></Protected>} />
      <Route path="/app/leave/:id" element={<Protected perm="leave:read"><LeaveDetailPage /></Protected>} />
      <Route path="/app/inventory/items/:id" element={<Protected perm="inventory:read"><ItemDetailPage /></Protected>} />
      <Route path="/app/tasks/scan/:code" element={<Protected perm="tasks:read"><TaskScanLanding /></Protected>} />
      <Route path="/app/tasks/:id" element={<Protected perm="tasks:read"><TaskDetailPage /></Protected>} />
      <Route path="/app/lms/courses/:id" element={<Protected perm="lms:read"><CourseDetailPage /></Protected>} />
      <Route path="/app/alumni/:id" element={<Protected perm="alumni:read"><AlumniDetailPage /></Protected>} />
      <Route path="/app/communication/announcements/:id" element={<Protected perm="communication:read"><AnnouncementDetailPage /></Protected>} />
      <Route path="/app/accounting/expenses/:id" element={<Protected perm="accounting:read"><ExpenseDetailPage /></Protected>} />
      <Route path="/app/frontoffice/visitors/:id" element={<Protected perm="frontoffice:read"><VisitorDetailPage /></Protected>} />
      <Route path="/app/bustracking/trips/:id" element={<Protected perm="bustracking:read"><BusTripDetailPage /></Protected>} />
      <Route path="/app/transport" element={<Protected perm="transport:read"><Transport /></Protected>} />
      <Route path="/app/library" element={<Protected perm="library:read"><Library /></Protected>} />
      <Route path="/app/hostel" element={<Protected perm="hostel:read"><Hostel /></Protected>} />
      <Route path="/app/compliance" element={<Protected perm="compliance:read"><Compliance /></Protected>} />
      <Route path="/app/help" element={<Protected perm="help:read"><HelpCenter /></Protected>} />
      <Route path="/app/website" element={<Protected perm="website:read"><WebsiteBuilder /></Protected>} />
      <Route path="/app/payments" element={<Protected anyPerms={["fees:read", "accounting:read", "payroll:read", "bank_details:read"]}><Payments /></Protected>} />
      <Route path="/app/communication" element={<Protected perm="communication:read"><Communication /></Protected>} />
      <Route path="/app/class-chat" element={<Protected perm="chat:read"><ClassChat /></Protected>} />
      <Route path="/app/chat-moderation" element={<Protected perm="chat:configure"><ChatModeration /></Protected>} />
      <Route path="/app/inbox" element={<Protected perm="communication:read"><Inbox /></Protected>} />
      <Route path="/app/assistant" element={<Protected perm="assistant:read"><Assistant /></Protected>} />
      <Route path="/app/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/app/login-history" element={<Protected><LoginHistory /></Protected>} />
      <Route path="/app/billing" element={<Protected perm="billing:configure"><Billing /></Protected>} />
      <Route path="/app/billing/return" element={<Protected perm="billing:configure"><BillingReturn /></Protected>} />
      {/* In-shell 404 for bad /app/* URLs — keeps staff in the app, not bounced to marketing. */}
      <Route path="/app/*" element={<Protected><NotFound /></Protected>} />
      {/* UX-03/PGS-4: unknown MARKETING urls render a branded, noindexed 404 with recovery links
          (no silent homepage redirect / soft-404). Tenant sites keep the bounce to their own
          homepage — the company 404 chrome would be the wrong brand on a school's domain. */}
      <Route path="*" element={isTenantSite ? <Navigate to="/" replace /> : <MarketingNotFound />} />
    </Routes>
    <MarketingAira enabled={!isTenantSite && !isSensitivePublicRoute} />
    <CookieNotice enabled={!isTenantSite} />
    <MarketingAnalyticsTracker enabled={!isTenantSite} />
    </React.Suspense>
  );
}

function RoutedApplication() {
  // Sonner defaults to its light theme regardless of the app theme — a bright white card over a
  // dark app is the classic dark-mode elevation failure. useResolvedTheme is MutationObserver-
  // backed (same source Charts uses), so toasts follow live theme switches.
  const resolvedTheme = useResolvedTheme();
  return (
    <UnsavedWorkGuardBoundary>
      <Toaster position="top-right" richColors closeButton theme={resolvedTheme === "dark" ? "dark" : "light"} />
      <AppRouter />
      <InstallPWA />
    </UnsavedWorkGuardBoundary>
  );
}

// A data router gives form-heavy workspaces (notably AutomationBuilder) a real SPA navigation
// blocker. The route table remains in AppRouter so this migration does not duplicate or fork routes.
// v7_relativeSplatPath is opted in early (silences the deprecation warning that polluted every
// marketing page's console — baseline audit 2026-08-02).
//
// v7_startTransition is deliberately NOT opted in, and must not be re-added to silence its warning.
// It wraps every router state update in React.startTransition, which makes navigation INTERRUPTIBLE.
// The workspaces deep-link by writing a search param and then immediately awaiting a refetch
// (CompensationWorkspace.adoptResult: setSearchParams({record}) → await list.reload()), and the
// urgent updates from that reload discard the still-pending transition — so a just-created record's
// `?record=…` never lands, its drawer never opens, and the operator is left on the bare register with
// a success toast. Caught by e2e/compensation.spec.js and the HR record-drawer specs; the console
// warning is the lesser cost. Revisit only with react-router v7, where the behaviour is not optional.
const browserRouter = createBrowserRouter([{ path: "*", element: <RoutedApplication /> }], {
  future: { v7_relativeSplatPath: true },
});

export default function App() {
  return (
    <ErrorBoundary>
      {/* The shared query cache is mounted in AppLayout (the LAZY authenticated shell), not here:
          this component is eager, so a provider at the root shipped @tanstack/react-query in the
          marketing entry chunk the bundle budget guards. */}
      <I18nProvider>
        <AuthProvider>
          <RouterProvider router={browserRouter} />
        </AuthProvider>
      </I18nProvider>
    </ErrorBoundary>
  );
}
